// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import {
  MapPin,
  ExternalLink,
  X,
  ChevronDown,
  Search,
  Share2,
  ClipboardPaste,
} from "lucide-react";
import { Button, FormField, Input, Select, useToast } from "@chotca/ui";
import { ChotcaApiError } from "@chotca/sdk";
import {
  type ProvinceCode,
  VN_PROVINCES_2025,
  getWardsForProvince2025,
} from "@chotca/config";
import { getSdk } from "@chotca/api-client";

// AddressPicker — Google-Maps-link based.
//
// Why not autocomplete? Goong/Google Places autocomplete charges per request.
// Pasting a Google Maps share URL gives us an exact, free, structured result:
// the URL itself encodes lat/lng + the human-readable address. The server
// expands short links and parses `/place/<addr>/@<lat>,<lng>` for us.
//
// User flow:
//   1. Open Google Maps → search the place → "Share" → "Copy link".
//   2. Paste here → click "Lấy địa điểm".
//   3. Address / district / city auto-fill (read-only). Lat/lng come straight
//      from Google so we trust them as-is — no map preview / drag pin.

export type AddressValue = {
  address: string;
  // 2025 post-merger ward — auto-filled server-side by point-in-polygon,
  // editable. `wardProvinceCode` is the official new numeric province code.
  wardCode: string;
  wardName: string;
  wardProvinceCode: string;
  // Legacy admin fields — kept for the transition (write path / NOT NULL cols).
  district: string;
  city: string;
  provinceCode: ProvinceCode | null;
  provinceName: string | null;
  lat: number | null;
  lng: number | null;
  // Canonical Google Maps URL — persisted on OrgAddress so mobile "Open
  // Maps" deep-links to the branded place pin (name, photos, hours).
  mapUrl: string | null;
};

export type AddressPickerProps = {
  label?: React.ReactNode;
  description?: React.ReactNode;
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  required?: boolean;
  disabled?: boolean;
  error?: React.ReactNode;
};

export function AddressPicker({
  label,
  description,
  value,
  onChange,
  required,
  disabled,
  error,
}: AddressPickerProps) {
  const { toast } = useToast();
  const [pasted, setPasted] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  async function applyUrl() {
    const url = pasted.trim();
    if (!url) return;
    setBusy(true);
    try {
      const r = await getSdk().geocode.parseUrl(url);
      // Ward is auto-resolved server-side (point-in-polygon on lat/lng); null on
      // a miss → user picks manually. District/city kept for legacy write path.
      onChange({
        address: r.address,
        wardCode: r.wardCode ?? "",
        wardName: r.wardName ?? "",
        wardProvinceCode: r.wardProvinceCode ?? "",
        district: r.district ?? "",
        city: r.city ?? "",
        provinceCode: r.provinceCode,
        provinceName: r.provinceName,
        lat: r.lat,
        lng: r.lng,
        mapUrl: r.resolvedUrl ?? url,
      });
      setPasted("");
      toast({ tone: "success", title: "Đã lấy địa điểm từ Google Maps" });
    } catch (err) {
      const msg =
        err instanceof ChotcaApiError && err.message
          ? err.message
          : "Không nhận diện được link. Hãy thử dán link gốc từ Google Maps.";
      toast({
        tone: "error",
        title: "Không lấy được vị trí",
        description: msg,
      });
    } finally {
      setBusy(false);
    }
  }

  function clearAll() {
    onChange({
      address: "",
      wardCode: "",
      wardName: "",
      wardProvinceCode: "",
      district: "",
      city: "",
      provinceCode: null,
      provinceName: null,
      lat: null,
      lng: null,
      mapUrl: null,
    });
  }

  const hasValue = !!value.address;
  const wardOptions = React.useMemo(
    () =>
      getWardsForProvince2025(value.wardProvinceCode).map((w) => ({
        value: w.c,
        label: w.n,
      })),
    [value.wardProvinceCode],
  );

  return (
    <div className="space-y-3">
      <FormField
        label={label ?? "Địa chỉ làm việc"}
        description={description}
        error={error}
        optional={!required}
      >
        <Instructions />

        {!disabled ? (
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Input
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="https://maps.app.goo.gl/… hoặc https://www.google.com/maps/place/…"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void applyUrl();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              loading={busy}
              disabled={!pasted.trim()}
              onClick={() => void applyUrl()}
            >
              Lấy địa điểm
            </Button>
          </div>
        ) : null}
      </FormField>

      {hasValue ? (
        <div className="space-y-3 rounded-lg border border-smoke bg-mist/40 p-3">
          <div className="flex items-start gap-2">
            <MapPin size={16} className="mt-0.5 shrink-0 text-ink" />
            <p className="flex-1 text-xs text-ash">
              Hệ thống đã điền sẵn từ Google Maps. Bạn có thể chỉnh lại nếu chưa
              chính xác.
            </p>
            {!disabled ? (
              <button
                type="button"
                onClick={clearAll}
                aria-label="Xoá địa chỉ"
                className="p-1 text-ash hover:text-ink"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>

          <div className="space-y-2">
            <Input
              value={value.address}
              onChange={(e) => onChange({ ...value, address: e.target.value })}
              placeholder="Số nhà, tên đường, phường"
              disabled={disabled}
            />
            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                value={
                  wardOptions.some((o) => o.value === (value.wardCode || ""))
                    ? value.wardCode
                    : ""
                }
                onValueChange={(code) => {
                  const w = wardOptions.find((o) => o.value === code);
                  onChange({
                    ...value,
                    wardCode: code,
                    wardName: w?.label ?? "",
                  });
                }}
                placeholder={
                  value.wardProvinceCode
                    ? "Phường / xã"
                    : "Chọn tỉnh / thành trước"
                }
                options={wardOptions}
                invalid={!value.wardCode}
                disabled={disabled || !value.wardProvinceCode}
                className="w-full"
              />
              <Select
                value={value.wardProvinceCode ?? ""}
                onValueChange={(code) => {
                  const p = VN_PROVINCES_2025.find((sp) => sp.code === code);
                  onChange({
                    ...value,
                    wardProvinceCode: p?.code ?? "",
                    provinceName: p?.name ?? null,
                    wardCode:
                      p?.code === value.wardProvinceCode ? value.wardCode : "",
                    wardName:
                      p?.code === value.wardProvinceCode ? value.wardName : "",
                  });
                }}
                placeholder="Tỉnh / thành phố"
                options={VN_PROVINCES_2025.map((p) => ({
                  value: p.code,
                  label: p.name,
                }))}
                invalid={!value.wardProvinceCode}
                disabled={disabled}
                className="w-full"
              />
            </div>
          </div>

          {!value.wardProvinceCode || !value.wardCode ? (
            <p className="text-xs text-warning">
              Vui lòng chọn đầy đủ tỉnh / thành và phường / xã.
            </p>
          ) : null}

          {value.lat !== null && value.lng !== null ? (
            <p className="font-mono text-xs text-ash">
              GPS {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Instruction strip — keeps the user from getting lost on first use. Three
// visual steps with icons + a tiny URL hint so they recognise the right format.
function Instructions() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-smoke bg-mist/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-mist"
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ink text-paper">
          <MapPin size={14} />
        </span>
        <span className="flex-1 text-sm font-semibold text-ink">
          Cách lấy link địa điểm từ Google Maps
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-ash transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="border-t border-smoke bg-paper px-3.5 py-4">
          <ol className="grid gap-3 sm:grid-cols-3">
            <Step
              n={1}
              icon={<Search size={14} />}
              title={
                <>
                  Mở{" "}
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 font-semibold text-info underline-offset-2 hover:underline"
                  >
                    Google Maps
                    <ExternalLink size={11} />
                  </a>
                </>
              }
              body="Tìm đúng địa chỉ làm việc trên web hoặc app."
            />
            <Step
              n={2}
              icon={<Share2 size={14} />}
              title={
                <>
                  Bấm <strong className="font-semibold">Chia sẻ</strong> →{" "}
                  <strong className="font-semibold">Sao chép liên kết</strong>
                </>
              }
              body="Icon mũi tên trên thẻ địa điểm."
            />
            <Step
              n={3}
              icon={<ClipboardPaste size={14} />}
              title={
                <>
                  Dán &amp; bấm{" "}
                  <strong className="font-semibold">Lấy địa điểm</strong>
                </>
              }
              body="Hệ thống tự điền tên đường, quận, thành phố và toạ độ GPS."
            />
          </ol>

          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ash">
            <span>Link hợp lệ:</span>
            <code className="rounded bg-mist px-1.5 py-0.5 font-mono text-[11px] text-ink/70">
              maps.app.goo.gl/…
            </code>
            <span className="text-ash/70">hoặc</span>
            <code className="rounded bg-mist px-1.5 py-0.5 font-mono text-[11px] text-ink/70">
              google.com/maps/place/…
            </code>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  body,
}: {
  n: number;
  icon: React.ReactNode;
  title: React.ReactNode;
  body: React.ReactNode;
}) {
  return (
    <li className="relative rounded-lg border border-smoke bg-mist/30 p-3 pl-3">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink text-[11px] font-semibold text-paper">
          {n}
        </span>
        <span className="text-ash">{icon}</span>
      </div>
      <p className="text-sm leading-snug text-ink">{title}</p>
      <p className="mt-1 text-xs leading-snug text-ash">{body}</p>
    </li>
  );
}
